// "Something's wrong with this record" — the human half of the corrections
// queue (api/src/routes/data-quality.ts).
//
// Deliberately open to everyone with crm.use, not just admins. The person who
// discovers a phone number is dead is the person who dialled it, and they are
// usually not the person allowed to change it. Without this, the only route is
// to tell someone, in a channel nobody tracks — which is how a record stays
// wrong for a year while several people privately know it is.
//
// This writes NOTHING to the contact. It files the report; an admin decides.

import { useEffect, useState } from 'react'
import { Modal, Button, Field, TextArea, Select } from '@/components/ui'
import { useApiToken } from '@/auth/useApiToken'
import { api } from '@/api/client'

/** Only fields a reporter can meaningfully point at. `''` = "something else",
 *  which is why the note is the required part and this is not. */
const FIELDS: [value: string, label: string][] = [
  ['', 'Not about one particular field'],
  ['workEmail', 'Work email'],
  ['personalEmail', 'Personal email'],
  ['officePhone', 'Office phone'],
  ['cellPhone', 'Mobile'],
  ['title', 'Title'],
  ['department', 'Department'],
  ['accountId', 'Company / account'],
]

export function ReportIssueModal({ open, onClose, contactId, contactName }: {
  open: boolean; onClose: () => void; contactId: string; contactName: string
}) {
  const getToken = useApiToken()
  const [field, setField] = useState('')
  const [note, setNote] = useState('')
  const [suggested, setSuggested] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setField(''); setNote(''); setSuggested(''); setSent(false); setError(null)
  }, [open])

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      const token = await getToken()
      await api.reportDataIssue(token, {
        entityId: contactId,
        note: note.trim(),
        ...(field ? { field } : {}),
        ...(suggested.trim() ? { suggestedValue: suggested.trim() } : {}),
      })
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Report a problem — ${contactName}`}
      footer={sent
        ? <Button variant="primary" onClick={onClose}>Done</Button>
        : (
          <>
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={busy || !note.trim()} onClick={() => void submit()}>
              {busy ? 'Sending…' : 'Send report'}
            </Button>
          </>
        )}
    >
      {sent ? (
        <p className="mdup-intro">
          Thanks — this is in the Data corrections queue on the Contacts page.
          Nothing on the record has changed; an administrator will review it.
        </p>
      ) : (
        <>
          {error && <div className="mdup-error" role="alert"><span>{error}</span></div>}
          <Field label="What is wrong?" hint="Which part of the record — pick one if it applies.">
            <Select value={field} onChange={(e) => setField(e.target.value)}>
              {FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
          <Field label="Tell us what you know" required>
            <TextArea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. This number rings a disconnected line — she left the firm in June."
            />
          </Field>
          {field && (
            <Field label="Correct value" hint="Optional. Only if you know it — a guess here becomes a change someone applies.">
              <TextArea rows={2} value={suggested} onChange={(e) => setSuggested(e.target.value)} />
            </Field>
          )}
        </>
      )}
    </Modal>
  )
}
